/**
 * ``useProducersStatus`` — TanStack Query hook around
 * ``GET /api/query/producers/status``.
 *
 * The **live-status** source for the Platforms-list per-row content
 * (storyboard Flow 05 Frame 1). Returns one
 * :type:`ProducerStatusItem` per Producer with time-series data —
 * latest ``health_state`` (drives the status dot), ``last_status_time``
 * (the "last good" timestamp), and the 60-second rolling
 * ``status_count`` / ``detection_count`` (drive the rate columns). The
 * envelope carries ``window_seconds`` so the UI labels the rate
 * columns and computes ``count ÷ window_seconds`` client-side per
 * Flow 05 §"Behaviour notes" ("the BFF computes these … not in the
 * browser" — the BFF owns the *counts*; the UI derives the
 * per-second rate label only).
 *
 * # Why a sibling of ``useProducers`` rather than one fetch
 *
 * ``/api/query/producers`` is slow-moving Producer **metadata**
 * (changes on onboarding / offboarding, admin-rare); this endpoint
 * is the fast-moving **status** view (changes every second per
 * Producer). :class:`PlatformsPanel` merges the two on
 * ``producer_id`` — same client-side-merge pattern the codebase
 * already uses for snapshot + metadata. A Producer present in the
 * metadata list but absent here (registered but silent) renders
 * gracefully: 'unknown' dot, no last-good, zero rates.
 *
 * # Why a refetch interval (not SSE)
 *
 * Flow 05 §"Behaviour notes" describes an eventual SSE-driven
 * in-place merge. That in-place merge is a deferred follow-up; this
 * hook ships a short ``staleTime`` + a ``refetchInterval`` so the
 * per-row rates stay live without the SSE wiring. Mirrors
 * :func:`useProducerRates`'s half-window cadence reasoning.
 *
 * Cross-references:
 *   - Storyboard Flow 05 Frame 1 (per-row status dot / last-good /
 *     rate columns) + §"Behaviour notes" (BFF-computed counts).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - REQ-UI-051 (Platforms list as a selection-bus originator —
 *     the panel that consumes this hook).
 *   - REQ-AUTH-024..027 (operator+ role gate; matches the
 *     ``/api/query/producers`` gate).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

/**
 * The wire-shape response from ``GET /api/query/producers/status``,
 * re-exported from the generated OpenAPI types so consumers don't
 * have to dig into the ``components`` shape.
 */
export type ProducersStatusResponse = components["schemas"]["ProducersStatusResponse"];

/** One Producer's live-status summary (merge key: ``producer_id``). */
export type ProducerStatusItem = components["schemas"]["ProducerStatusItem"];

/**
 * Roles permitted to read ``/api/query/producers/status`` per
 * REQ-AUTH-024..027. Mirrors :const:`PRODUCERS_READER_ROLES` from
 * :file:`use-producers.ts` — the metadata + status endpoints share
 * the same operator+ RBAC gate.
 */
export const PRODUCERS_STATUS_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/**
 * Default count window (seconds). 60 s matches Flow 05's
 * "msgs/s last 60s" rate columns and the BFF's default
 * ``window_seconds``.
 */
export const PRODUCERS_STATUS_WINDOW_SECONDS = 60;

/**
 * ``staleTime`` for the producers-status query.
 *
 * 5 s — this is LIVE data, so a short staleTime keeps rates fresh
 * across re-renders without thrashing the cache. Paired with the
 * refetch interval below for background liveness.
 */
export const PRODUCERS_STATUS_STALE_TIME_MS = 5_000;

/**
 * Background refetch cadence. 10 s — roughly a sixth of the 60 s
 * window, so transitions (a platform going silent, a rate climbing)
 * surface within ~10 s without the SSE wiring (deferred follow-up).
 */
export const PRODUCERS_STATUS_REFETCH_MS = 10_000;

/**
 * The TanStack Query key for the producers-status query. Window-keyed
 * so a future window-size control (Flow 05 Q1, post-MVP) caches each
 * window independently. Exported for test reach.
 */
export function producersStatusQueryKey(windowSeconds: number): readonly unknown[] {
  return ["producers-status", windowSeconds] as const;
}

/** TanStack Query result alias for this hook. */
export type UseProducersStatusResult = UseQueryResult<ProducersStatusResponse, ApiError>;

/**
 * Type guard for one :type:`ProducerStatusItem`. ``last_status_time``
 * is ``string | null`` on the wire (null for a Producer with events
 * but no status, or nothing in the tier).
 */
function isProducerStatusItem(value: unknown): value is ProducerStatusItem {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.producer_id === "string" &&
    typeof obj.health_state === "number" &&
    (obj.last_status_time === null || typeof obj.last_status_time === "string") &&
    typeof obj.status_count === "number" &&
    typeof obj.detection_count === "number"
  );
}

/**
 * Type guard for the wire-shape response. Required because the
 * generated OpenAPI types describe the contract but don't ship
 * runtime validators — boundary-validation per CLAUDE.md §8.1
 * (silent failures forbidden).
 */
export function isProducersStatusResponse(value: unknown): value is ProducersStatusResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (!Array.isArray(obj.items)) return false;
  if (typeof obj.window_seconds !== "number") return false;
  if (typeof obj.computed_at !== "string") return false;
  return obj.items.every(isProducerStatusItem);
}

/**
 * Fetch the per-Producer live-status summaries for the Platforms
 * list.
 *
 * The query is disabled when the caller lacks an operator+ role —
 * the BFF would 403, but the upstream gate avoids the noisy
 * round-trip (defense-in-depth alongside Stream C's panel-registry
 * role filter).
 *
 * @returns TanStack Query result whose ``data`` is the typed
 *     :type:`ProducersStatusResponse` when the fetch succeeds.
 *
 * Cross-references:
 *   - Storyboard Flow 05 Frame 1 + §"Behaviour notes".
 *   - REQ-UI-026 / REQ-UI-051.
 *   - REQ-AUTH-024..027.
 */
export function useProducersStatus(): UseProducersStatusResult {
  const session = useSession();
  const enabled = session.hasAnyRole(PRODUCERS_STATUS_READER_ROLES);
  const windowSeconds = PRODUCERS_STATUS_WINDOW_SECONDS;

  return useQuery<ProducersStatusResponse, ApiError>({
    queryKey: producersStatusQueryKey(windowSeconds),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(
        `/api/query/producers/status?window_seconds=${windowSeconds}`,
        { signal },
      );
      if (!isProducersStatusResponse(raw)) {
        // Boundary-validation failure — the BFF surface shifted.
        // Per CLAUDE.md §8.1 surface loudly rather than rendering
        // stale / blank rates silently.
        console.error(
          "[meridian/platforms/use-producers-status] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "producers-status response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: PRODUCERS_STATUS_STALE_TIME_MS,
    refetchInterval: enabled ? PRODUCERS_STATUS_REFETCH_MS : false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
